import { useTranslation } from "react-i18next"

import type { EditorTool } from "./editor-types"

import { Circle, Eraser, Paintbrush, Redo2, Square, Undo2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Slider } from "@/components/ui/slider"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface EditorToolbarProps {
  activeTool: EditorTool
  brushSize: number
  maskOpacity: number
  canUndo: boolean
  canRedo: boolean
  onToolChange: (tool: EditorTool) => void
  onBrushSizeChange: (size: number) => void
  onMaskOpacityChange: (opacity: number) => void
  onUndo: () => void
  onRedo: () => void
  onClearMask: () => void
}

const TOOLS: { tool: EditorTool; icon: typeof Paintbrush; labelKey: string }[] = [
  { tool: "brush", icon: Paintbrush, labelKey: "brush" },
  { tool: "rectangle", icon: Square, labelKey: "rectangle" },
  { tool: "ellipse", icon: Circle, labelKey: "ellipse" },
]

export function EditorToolbar({
  activeTool,
  brushSize,
  maskOpacity,
  canUndo,
  canRedo,
  onToolChange,
  onBrushSizeChange,
  onMaskOpacityChange,
  onUndo,
  onRedo,
  onClearMask,
}: EditorToolbarProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.imageEditor" })

  return (
    <div className="flex w-14 flex-col items-center gap-1 border-r p-2 sm:w-56 sm:items-stretch sm:gap-2 sm:p-3">
      {/* Tools */}
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-col gap-1">
          {TOOLS.map(({ tool, icon: Icon, labelKey }) => (
            <Tooltip key={tool}>
              <TooltipTrigger asChild>
                <Button
                  className="justify-start gap-2"
                  size="sm"
                  variant={activeTool === tool ? "default" : "ghost"}
                  onClick={() => onToolChange(tool)}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t(labelKey)}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t(labelKey)}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      <Separator className="my-2" />

      {/* Brush size */}
      {activeTool === "brush" && (
        <div className="hidden space-y-2 sm:block">
          <Label className="text-xs">
            {t("brushSize")}: {brushSize}px
          </Label>
          <Slider
            max={100}
            min={2}
            step={1}
            value={[brushSize]}
            onValueChange={([v]) => onBrushSizeChange(v)}
          />
        </div>
      )}

      {/* Mask opacity */}
      <div className="hidden space-y-2 sm:block">
        <Label className="text-xs">
          {t("maskOpacity")}: {Math.round(maskOpacity * 100)}%
        </Label>
        <Slider
          max={100}
          min={10}
          step={5}
          value={[maskOpacity * 100]}
          onValueChange={([v]) => onMaskOpacityChange(v / 100)}
        />
      </div>

      <Separator className="my-2" />

      {/* Undo/Redo */}
      <div className="flex flex-col gap-1 sm:flex-row">
        <Button disabled={!canUndo} size="sm" title={t("undo")} variant="ghost" onClick={onUndo}>
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button disabled={!canRedo} size="sm" title={t("redo")} variant="ghost" onClick={onRedo}>
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Clear mask */}
      <Button className="mt-auto" size="sm" variant="outline" onClick={onClearMask}>
        <span className="hidden sm:inline">{t("clearMask")}</span>
        <Eraser className="h-4 w-4 sm:hidden" />
      </Button>
    </div>
  )
}
